import React, { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Divider from '@mui/material/Divider'

import RolesPermissionsService, { IPermission } from '../../services/RolesPermissionsService'
import RolesService from '../../services/RolesService'
import api from '../../config/api'
import Banner from '../../common/components/banner'
import { t } from 'i18next'

import banner from '../../assets/images/banners/banner_title_page_list_roles.png'

type Params = {
  id?: string
}

type IRole = {
  id: number
  name: string
}

export default function RolePermissionsV2() {
  const { id } = useParams<Params>()
  const navigate = useNavigate()

  const [roles, setRoles] = useState<IRole[]>([])
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null)

  const [allPermissions, setAllPermissions] = useState<IPermission[]>([])
  const [assignedPermissions, setAssignedPermissions] = useState<number[]>([])

  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load roles list and all permissions at mount
  useEffect(() => {
    setLoading(true)
    setError(null)

    const pRoles = RolesService.getAll(1, 0)
    const pAllPerms = api.get<IPermission[]>('allPermissions')

    Promise.all([pRoles, pAllPerms])
      .then(([rolesResp, permsResp]) => {
        // rolesResp.data may be paginated structure; try to extract array
        const rolesData: any = rolesResp.data?.data ?? rolesResp.data ?? []
        const mapped: IRole[] = (rolesData || []).map((r: any) => ({ id: r.id, name: r.name }))
        setRoles(mapped)
        setAllPermissions(permsResp.data || [])

        // initial selection: route param or first role
        const paramId = id ? parseInt(id, 10) : NaN
        if (!isNaN(paramId)) {
          setSelectedRoleId(paramId)
        } else if (mapped.length > 0) {
          setSelectedRoleId(mapped[0].id)
        }
      })
      .catch((e) => setError(e?.message || 'Erro ao carregar dados'))
      .finally(() => setLoading(false))
  }, [id])

  // When selected role changes, load its permissions
  useEffect(() => {
    if (!selectedRoleId) return
    setLoading(true)
    setError(null)

    RolesPermissionsService.getPermissions(selectedRoleId)
      .then((resp) => {
        const rolePerms: IPermission[] = resp.data || []
        setAssignedPermissions(rolePerms.map((p) => p.id))
      })
      .catch((e) => setError(e?.message || 'Erro ao carregar permissões da role'))
      .finally(() => setLoading(false))
  }, [selectedRoleId])

  const togglePermission = (permId: number) => {
    setAssignedPermissions((prev) => (prev.includes(permId) ? prev.filter((p) => p !== permId) : [...prev, permId]))
  }

  const handleSave = async () => {
    if (!selectedRoleId) return
    setSaving(true)
    setError(null)
    try {
      await RolesPermissionsService.syncPermissions(selectedRoleId, assignedPermissions)
      // keep user on page and maybe show feedback later; here we simply refresh
      RolesPermissionsService.getPermissions(selectedRoleId).then((r) => setAssignedPermissions((r.data || []).map((p: IPermission) => p.id)))
    } catch (e: any) {
      setError(e?.message || 'Erro ao salvar permissões')
    } finally {
      setSaving(false)
    }
  }

  // Group permissions by suffix after last underscore
  const groupedPermissions = useMemo(() => {
    const map = new Map<string, IPermission[]>()
    allPermissions.forEach((perm) => {
      const name = perm.name || ''
      const parts = name.split('_')
      const key = parts.length > 1 ? parts.slice(-1)[0] : name
      const arr = map.get(key) || []
      arr.push(perm)
      map.set(key, arr)
    })
    // convert to array of [group, perms] sorted by group
    return Array.from(map.entries()).sort((a, b) => a[0].localeCompare(b[0]))
  }, [allPermissions])

  if (loading) return <CircularProgress />

  return (
    <Box sx={{ p: 3 }}>
      <Banner type="medium" image={banner} title={t('banner_title_page_list_roles')} subtitle={t('banner_subtitle_page_list_roles')} />

      <Typography variant="h5" gutterBottom>
        Gerenciar Permissões
      </Typography>

      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 3 }}>
        {/* Left: Roles list */}
        <Box sx={{ width: '30%', borderRight: '1px solid #e0e0e0', pr: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Roles
          </Typography>
          <List dense>
            {roles.map((role) => (
              <div key={role.id}>
                <ListItem disablePadding>
                  <ListItemButton selected={selectedRoleId === role.id} onClick={() => setSelectedRoleId(role.id)}>
                    <ListItemText primary={role.name} />
                  </ListItemButton>
                </ListItem>
                <Divider />
              </div>
            ))}
          </List>
        </Box>

        {/* Right: Grouped permissions */}
        <Box sx={{ flex: 1 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Permissões (agrupadas por sufixo)
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {groupedPermissions.map(([group, perms]) => (
              <Box key={group} sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography sx={{ fontWeight: 'bold', minWidth: 140 }}>{group}</Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {perms.map((perm) => (
                    <FormControlLabel
                      key={perm.id}
                      control={<Checkbox checked={assignedPermissions.includes(perm.id)} onChange={() => togglePermission(perm.id)} />}
                      label={perm.description || perm.name}
                    />
                  ))}
                </Box>
              </Box>
            ))}
          </Box>

          <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
            <Button variant="contained" color="primary" onClick={handleSave} disabled={saving || !selectedRoleId}>
              {saving ? 'Salvando...' : 'Salvar'}
            </Button>
            <Button variant="outlined" onClick={() => navigate(-1)}>
              Voltar
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
